import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { AuditQuery } from "../../shared/audit-schemas";
import { listOperationsAudit, listWorkspaceAudit } from "../api/audit-client";
import { AuditHeader } from "./AuditHeader";
import { AuditFilters } from "./AuditFilters";
import { AuditCheckedAt, AuditPagination, AuditResults } from "./AuditResults";

type Audience = (
  | { audience: "operations"; organizationId?: string }
  | { audience: "customer"; organizationId: string }
) & { embedded?: boolean };

/** A workspace switch resets filters, pagination, and the rendered query. */
export function AuditExplorer(props: Audience) {
  return (
    <AuditExplorerContent key={`${props.audience}:${props.organizationId ?? "all"}`} {...props} />
  );
}

function AuditExplorerContent(props: Audience) {
  const [query, setQuery] = useState<AuditQuery>({ pageSize: 10, view: "activity" });
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [filterRevision, setFilterRevision] = useState(0);
  const [revision, setRevision] = useState(0);
  const cursor = cursors.at(-1);
  const input = { ...query, ...(cursor ? { cursor } : {}) };
  const audit = useQuery({
    queryKey: ["audit", props.audience, props.organizationId ?? null, input, revision],
    queryFn: () =>
      props.audience === "customer"
        ? listWorkspaceAudit(props.organizationId, input)
        : listOperationsAudit({
            ...input,
            ...(props.organizationId ? { organizationId: props.organizationId } : {}),
          }),
    staleTime: 60_000,
    retry: false,
  });
  function change(next: AuditQuery) {
    setQuery(next);
    setCursors([undefined]);
  }
  function refresh() {
    setCursors([undefined]);
    setRevision((value) => value + 1);
  }
  return (
    <section aria-labelledby="audit-title" className="mx-auto w-full max-w-[1120px]">
      <AuditHeader
        query={query}
        audience={props.audience}
        organizationId={props.organizationId}
        embedded={props.embedded}
        refresh={refresh}
        fetching={audit.isFetching}
      />
      <AuditFilters
        key={filterRevision}
        query={query}
        onChange={change}
        operations={props.audience === "operations"}
      />
      <AuditCheckedAt audit={audit} />
      <div
        aria-busy={audit.isFetching}
        aria-live="polite"
        className="overflow-hidden rounded-lg border bg-card"
      >
        <AuditResults
          audit={audit}
          onRecordHistory={(event) => {
            setFilterRevision((value) => value + 1);
            change({
              ...query,
              view: "all",
              application: event.appInstanceId,
              record: event.subjectKey,
              q: undefined,
              person: undefined,
              action: undefined,
              outcome: undefined,
            });
          }}
          showCustomer={props.audience === "operations" && !props.organizationId}
        />
      </div>
      <AuditPagination audit={audit} cursors={cursors} onChange={setCursors} />
    </section>
  );
}
